// SabrBand port — verbatim from immersive_gui/ui/src/components/SabrBand.jsx.
// Section title is "Surface Fit" (not "Surface Fit (SABR)" — the "(SABR)"
// lives in the sub-header inside the card per the MVP).
//
// Layout: one card with border + radius + padding.
//   Sub-header row: "SABR (Approx)"  (10px, weight 500, text.primary)
//   Below: a single horizontal flex row of three label-value pairs:
//     vol_0  <small label> <value>     Xi <label> <value>     Rho <label> <value>
//   All values mono / tabular-nums; rho uses signed-percent so a "+" prefix
//   is shown for positive values per reviewer Item 7.
import { FC, useContext } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import Number from '../../number';
import { PricingContext } from '../../../../providers/exotics-pricing-provider';

import './exotics-sabr-band-styles.scss';

interface Props {
  instrumentId: string;
}

const Label: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontSize: 9,
      color: 'text.secondary',
      letterSpacing: '0.04em',
      mr: 0.5,
    }}
  >
    {children}
  </Box>
);

const ExoticsSabrBandContent: FC<Props> = ({ instrumentId }) => {
  const { results, errors, loading } = useContext(PricingContext);
  const sabr = results[instrumentId]?.sabr;
  const err = errors[instrumentId];
  const empty =
    !!err || !sabr || ![sabr.vol_0, sabr.xi, sabr.rho].every((v) => globalThis.Number.isFinite(v)); // `Number` is the imported component here, so qualify with globalThis
  void loading;

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(40, 114, 226, 0.06)',
        border: '1px solid #1f3645',
        borderRadius: '6px',
        px: 1.25,
        py: 0.75,
      }}
      className="exotics-sabr-band-content"
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 500,
          color: 'text.primary',
          mb: 0.5,
        }}
      >
        SABR (Approx)
      </Typography>
      {empty ? (
        <Box sx={{ fontSize: 10, color: 'text.disabled' }}>n/a</Box>
      ) : (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          sx={{
            fontFamily: 'Inter',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* MVP SABR values are 10px (micro role) — smaller than the
                Pricing & Risk values above per the platform hierarchy. */}
          <Box>
            <Label>vol_0</Label>
            <Number value={sabr!.vol_0} format="percent" role="micro" />
          </Box>
          <Box>
            <Label>Xi</Label>
            <Number value={sabr!.xi} format="price-4dp" role="micro" />
          </Box>
          <Box>
            <Label>Rho</Label>
            <Number value={sabr!.rho} format="signed-percent" role="micro" />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default ExoticsSabrBandContent;
